<template>
  <b-container>
    <tool-header
      tool-name="Team Mashup Configurator"
      class="mb-3" />

    <b-row>
      <b-col cols="6">
        <b-form-select
          v-model="teamId"
          class="mb-3"
          :options="teams"
          :disabled="isLoadingData"
          value-field="fileCode"
          text-field="name"
          @change="dispatchLoadMashupData" />
      </b-col>
      <b-col>
        <b-form-radio-group
          v-model="locale"
          buttons
          button-variant="outline-primary"
          class="MashupRadioGroup MashupRadioGroup--FullWidthGroup"
          :disabled="isLoadingData">
          <b-form-radio
            v-model="locale"
            value="en">
            EN
          </b-form-radio>
          <b-form-radio
            v-model="locale"
            value="es">
            ES
          </b-form-radio>
          <b-form-radio
            v-model="locale"
            value="ja">
            JA
          </b-form-radio>
        </b-form-radio-group>
      </b-col>
      <b-col>
        <b-form-radio-group
          v-model="platform"
          buttons
          button-variant="outline-primary"
          class="MashupRadioGroup MashupRadioGroup--FullWidthGroup"
          :disabled="isLoadingData">
          <b-form-radio
            v-model="platform"
            value="ios">
            <i class="fab fa-apple" />
          </b-form-radio>
          <b-form-radio
            v-model="platform"
            value="android">
            <i class="fab fa-android" />
          </b-form-radio>
          <b-form-radio
            v-model="platform"
            value="unfiltered">
            <i class="fas fa-times" />
          </b-form-radio>
        </b-form-radio-group>
      </b-col>
      <b-col>
        <b-form-radio-group
          v-model="device"
          buttons
          button-variant="outline-primary"
          class="MashupRadioGroup MashupRadioGroup--FullWidthGroup"
          :disabled="isLoadingData">
          <b-form-radio
            v-model="device"
            value="phone">
            <i class="fas fa-mobile-alt" />
          </b-form-radio>
          <b-form-radio
            v-model="device"
            value="tablet">
            <i class="fas fa-tablet" />
          </b-form-radio>
          <b-form-radio
            v-model="device"
            value="unfiltered">
            <i class="fas fa-times" />
          </b-form-radio>
        </b-form-radio-group>
      </b-col>
    </b-row>
    <b-row v-show="isLoadingData">
      <b-col>
        <div class="text-center mt-3 mb-3">
          <b-spinner label="Loading..." />
        </div>
      </b-col>
    </b-row>
    <b-row v-show="!isLoadingData">
      <b-col v-if="teamId">
        <div>
          <b-card no-body>
            <b-tabs
              card
              fill
              pills>
              <b-tab
                title="Config"
                active>
                <template v-if="platform === 'ios' || platform === 'unfiltered'">
                  <h4>iOS Config</h4>
                  <hr class="mt-0">
                  <IosConfig />
                </template>
                <div
                  v-if="platform ==='unfiltered'"
                  class="my-4" />
                <template v-if="platform === 'android' || platform === 'unfiltered'">
                  <h4>Android Config</h4>
                  <hr class="mt-0">
                  <AndroidConfig />
                </template>
              </b-tab>
              <b-tab title="Insider">
                <Insider />
              </b-tab>
              <b-tab title="More From">
                <MoreFrom />
              </b-tab>
            </b-tabs>
          </b-card>
        </div>
      </b-col>
    </b-row>
    <b-row
      v-if="teamId"
      v-show="!isLoadingData"
      class="mt-3 mb-3"
      align-h="end">
      <b-col cols="auto">
        <b-button
          class="mr-2"
          variant="outline-success"
          :disabled="isLoadingData"
          @click="save">
          {{ betaEnvName }}
        </b-button>
        <b-button
          variant="success"
          :disabled="isLoadingData || !hasSavedToBeta"
          @click="promote">
          Publish To {{ liveEnvName }}
        </b-button>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import ToolHeader from '@/components/ToolHeader'
import TeamOptions from '@/assets/teams'
import MoreFrom from '@/components/TeamMashup/MoreFrom'
import Insider from '@/components/TeamMashup/Insider'
import AndroidConfig from '@/components/TeamMashup/AndroidConfig'
import IosConfig from '@/components/TeamMashup/IosConfig'
import {TeamMashup} from '@/services/api'
import {mapFields} from 'vuex-map-fields'
import {mapGetters} from 'vuex'

export default {
  name: "TeamMashup",
  components: {
    ToolHeader,
    MoreFrom,
    Insider,
    AndroidConfig,
    IosConfig,
  },
  data() {
    return {
      isLoadingData: false,
      hasSavedToBeta: false,
    }
  },
  computed: {
    teams: () => {
      return TeamOptions
    },
    ...mapGetters([
      'betaEnvName',
      'liveEnvName',
      'model',
      'raw',
      'paths']),
    ...mapFields([
      'teamId',
      'locale',
      'platform',
      'device',
    ]),
  },
  created() {
    this.teamId = null
  },
  methods: {
    dispatchLoadMashupData: async function () {
      this.isLoadingData = true
      await this.$nextTick()

      try {
        const {locale, device, platform, teamId} = this

        // on initial load there will be no teamId selected
        if (teamId) {
          await this.$store.dispatch('loadMashupData', {
            locale,
            device,
            platform,
            teamId,
          })
        }
      } finally {
        this.isLoadingData = false
      }
    },
    save: async function () {
      this.isLoadingData = true
      await this.$nextTick()

      try {
        await TeamMashup.updateMashup({
          teamId: this.teamId,
          state: this.raw,
          model: this.model,
        })

        this.$alert.success(`Successfully saved data!`)
        this.hasSavedToBeta = true
      } finally {
        this.isLoadingData = false
      }
    },
    promote: async function () {
      this.isLoadingData = true
      await this.$nextTick()

      try {
        await TeamMashup.promote({
          paths: this.paths,
        })

        this.$alert.success(`Successfully promoted to production!`)
        this.hasSavedToBeta = false
      } finally {
        this.isLoadingData = false
      }
    },
  },
}
</script>

<style scoped>
.MashupRadioGroup--FullWidthGroup {
  width: 100%;
}
</style>
